import { FC, useEffect } from 'react';
import { createZodFormResolver, FormInputField, lt, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getReservationsByIds } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { useCommanderSelector } from '@mews-commander/utils';
import { FormLayout } from '../../FormLayout';
import { PurchaseOrderNumberFormKeys, PurchaseOrderNumberFormSchema, PurchaseOrderNumberFormValues } from './types';
import { useChangePurchaseOrderNumber } from './useChangePurchaseOrderNumber';
import { useDefaultPurchaseOrderNumber } from './useDefaultPurchaseOrderNumber';

interface PurchaseOrderNumberFormProps {
    reservationIds: string[];
}

export const PurchaseOrderNumberForm: FC<PurchaseOrderNumberFormProps> = ({
    reservationIds,
}: PurchaseOrderNumberFormProps) => {
    const currentEnterpriseId = useCurrentEnterpriseId();

    const reservations = useCommanderSelector((state) =>
        getReservationsByIds(state, { ids: reservationIds, enterpriseId: currentEnterpriseId })
    );

    const { changePurchaseOrderNumber, isLoading } = useChangePurchaseOrderNumber(reservations);
    const defaultPurchaseOrderNumber = useDefaultPurchaseOrderNumber(reservationIds);

    const { renderFormContent, setValue } = useFormControls<PurchaseOrderNumberFormValues>({
        onSubmit: changePurchaseOrderNumber,
        resolver: createZodFormResolver(PurchaseOrderNumberFormSchema),
        dataTestForm: 'po-number-form',
        defaultValues: {
            [PurchaseOrderNumberFormKeys.purchaseOrderNumber]: defaultPurchaseOrderNumber,
        },
    });

    useEffect(() => {
        setValue(PurchaseOrderNumberFormKeys.purchaseOrderNumber, defaultPurchaseOrderNumber);
    }, [defaultPurchaseOrderNumber, setValue]);

    return renderFormContent(
        <FormLayout vertical={false} loading={isLoading}>
            <FormInputField
                label={lt(TextKey.ReservationPurchaseOrderNumber)}
                name={PurchaseOrderNumberFormKeys.purchaseOrderNumber}
            />
        </FormLayout>
    );
};
